import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Clock, Calendar, AlertTriangle, CheckCircle2, Phone,
  ArrowUpRight, ArrowRight, Share2, BookOpen, PawPrint, Zap, Download, FileText, ChevronRight, Heart,
} from "lucide-react";
import { ShareButtonsRow } from "@/components/custom/ShareButtons";
import { SEO } from "@/components/SEO";
import BLOG_ARTICLES from "@/data/blogArticles";
import { C, DISPLAY, BODY, MONO } from "@/styles/editorial";

const PHASE_LABELS = { before: "ANTES", during: "DURANTE", after: "DESPUÉS" };
const PHASE_COLORS = { before: C.blue, during: C.red, after: C.olive };

/* ═══════════════════ ANIMATED VALUE ═══════════════════ */
function AnimatedValue({ value }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [display, setDisplay] = useState("0");

  const match = value.match(/([\d.]+)/);
  const hasNum = !!match;
  const target = hasNum ? parseInt(match[1].replace(/\./g, ""), 10) : 0;
  const prefix = hasNum ? value.slice(0, match.index) : "";
  const suffix = hasNum ? value.slice(match.index + match[1].length) : "";

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); obs.unobserve(el); }
    }, { threshold: 0.2 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!visible || !hasNum) return;
    const dur = 1800;
    const start = performance.now();
    const fmt = (n) => Math.round(n).toLocaleString("es-ES");
    const tick = (now) => {
      const p = Math.min((now - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(prefix + fmt(eased * target) + suffix);
      if (p < 1) requestAnimationFrame(tick);
      else setDisplay(value);
    };
    requestAnimationFrame(tick);
  }, [visible, hasNum, target, prefix, suffix, value]);

  return <span ref={ref}>{visible ? display : (hasNum ? prefix + "0" + suffix : value)}</span>;
}

/* ═══════════════════ REVEAL ON SCROLL ═══════════════════ */
function RevealOnScroll({ children, delay = 0 }) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVis(true); obs.unobserve(el); }
    }, { threshold: 0.06 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} style={{
      opacity: vis ? 1 : 0,
      transform: vis ? "translateY(0)" : "translateY(28px)",
      transition: `opacity 0.7s cubic-bezier(0.22,1,0.36,1) ${delay}s, transform 0.7s cubic-bezier(0.22,1,0.36,1) ${delay}s`
    }}>{children}</div>
  );
}

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });
}

/* ═══════════════════ SECTION RENDERERS ═══════════════════ */

function SectionNarrative({ content, lead }) {
  return (
    <div className="max-w-[680px]">
      {lead && (
        <p className="text-lg md:text-xl leading-relaxed mb-4" style={{ fontFamily: BODY, color: C.navy, fontWeight: 600 }}>{lead}</p>
      )}
      {(Array.isArray(content) ? content : [content]).map((p, i) => (
        <p key={i} className="text-base leading-[1.8] mb-5 last:mb-0" style={{ fontFamily: BODY, color: "#444" }}>{p}</p>
      ))}
    </div>
  );
}

function SectionImage({ src, alt, caption, wide }) {
  return (
    <figure className={wide ? "-mx-6 md:-mx-12" : ""} style={{ margin: wide ? "0 -24px" : undefined }}>
      <div style={{ borderRadius: wide ? 0 : 6, overflow: "hidden" }}>
        <img
          src={src} alt={alt}
          className={`w-full object-cover ${wide ? "h-[280px] md:h-[420px]" : "h-[240px] md:h-[360px]"}`}
          loading="lazy"
        />
      </div>
      {caption && (
        <figcaption className="mt-3 text-[11px] tracking-wider" style={{ fontFamily: MONO, color: "#999" }}>
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

function SectionPullQuote({ text, attribution, color = C.red }) {
  return (
    <div className="py-10 md:py-14 px-8 md:px-12 -mx-6 md:-mx-0" style={{ background: color, borderRadius: 0 }}>
      <blockquote>
        <p className="text-xl md:text-2xl lg:text-3xl leading-[1.25] tracking-tight" style={{ fontFamily: DISPLAY, color: C.white }}>
          &ldquo;{text}&rdquo;
        </p>
        {attribution && (
          <footer className="mt-5">
            <p className="text-[11px] tracking-[0.2em] uppercase" style={{ fontFamily: MONO, color: "rgba(255,255,255,0.6)" }}>
              — {attribution}
            </p>
          </footer>
        )}
      </blockquote>
    </div>
  );
}

function SectionStat({ items, bg = C.navy }) {
  return (
    <div className="py-14 px-8 md:px-14" style={{ background: bg, borderRadius: 6 }}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
        {items.map((s, i) => (
          <div key={i} style={{ borderLeft: `3px solid ${s.color || C.orange}`, paddingLeft: 20 }}>
            <p className="text-4xl md:text-5xl font-bold" style={{ fontFamily: DISPLAY, color: s.color || C.orange }}>
              <AnimatedValue value={s.value} />
            </p>
            <p className="text-sm mt-3 leading-relaxed" style={{ fontFamily: BODY, color: "rgba(255,255,255,0.7)" }}>{s.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ColorBlockCard({ children }) {
  const [h, setH] = useState(false);
  return (
    <div
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      className="p-5 md:p-6"
      style={{
        background: h ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.13)",
        borderRadius: 6,
        backdropFilter: "blur(4px)",
        transform: h ? "translateY(-4px)" : "translateY(0)",
        transition: "transform 0.3s cubic-bezier(0.22,1,0.36,1), background 0.3s ease, box-shadow 0.3s ease",
        boxShadow: h ? "0 8px 24px rgba(0,0,0,0.15)" : "none",
        cursor: "default",
      }}
    >
      {children}
    </div>
  );
}

function SectionColorBlock({ title, content, items, color = C.red, icon: Icon }) {
  return (
    <div className="py-12 px-8 md:px-14" style={{ background: color, borderRadius: 6 }}>
      {title && (
        <div className="flex items-center gap-3 mb-6">
          {Icon && <Icon className="w-6 h-6" style={{ color: "rgba(255,255,255,0.7)" }} />}
          <h3 className="text-xl md:text-2xl font-bold" style={{ fontFamily: DISPLAY, color: C.white }}>{title}</h3>
        </div>
      )}
      {content && (
        <p className="text-base md:text-lg leading-[1.7] mb-8" style={{ fontFamily: BODY, color: "rgba(255,255,255,0.9)" }}>{content}</p>
      )}
      {items && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((item, i) => {
            const isObj = typeof item === "object";
            const itemTitle = isObj ? item.title : null;
            const desc = isObj ? item.desc : (typeof item === "string" ? item : item.text);
            return (
              <ColorBlockCard key={i}>
                <span className="text-[10px] font-bold tracking-wider block mb-2" style={{ fontFamily: MONO, color: "rgba(255,255,255,0.4)" }}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                {itemTitle && (
                  <p className="text-base md:text-lg font-bold mb-1.5" style={{ fontFamily: DISPLAY, color: C.white }}>{itemTitle}</p>
                )}
                <p className={`${itemTitle ? "text-sm" : "text-sm md:text-base"} leading-[1.65] font-medium`} style={{ fontFamily: BODY, color: itemTitle ? "rgba(255,255,255,0.82)" : C.white }}>{desc}</p>
              </ColorBlockCard>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SectionSplit({ left, right, reverse }) {
  return (
    <div className={`grid md:grid-cols-2 gap-8 items-center ${reverse ? "" : ""}`}>
      <div className={reverse ? "md:order-2" : ""}>
        {left.type === "image" ? (
          <div style={{ borderRadius: 6, overflow: "hidden" }}>
            <img src={left.src} alt={left.alt || ""} className="w-full h-[280px] object-cover" loading="lazy" />
          </div>
        ) : (
          <div>
            {left.title && <h3 className="text-lg font-bold mb-3" style={{ fontFamily: DISPLAY, color: C.navy }}>{left.title}</h3>}
            {(Array.isArray(left.content) ? left.content : [left.content]).map((p, i) => (
              <p key={i} className="text-sm leading-[1.7] mb-3 last:mb-0" style={{ color: "#555" }}>{p}</p>
            ))}
          </div>
        )}
      </div>
      <div className={reverse ? "md:order-1" : ""}>
        {right.type === "image" ? (
          <div style={{ borderRadius: 6, overflow: "hidden" }}>
            <img src={right.src} alt={right.alt || ""} className="w-full h-[280px] object-cover" loading="lazy" />
          </div>
        ) : (
          <div>
            {right.title && <h3 className="text-lg font-bold mb-3" style={{ fontFamily: DISPLAY, color: C.navy }}>{right.title}</h3>}
            {(Array.isArray(right.content) ? right.content : [right.content]).map((p, i) => (
              <p key={i} className="text-sm leading-[1.7] mb-3 last:mb-0" style={{ color: "#555" }}>{p}</p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SectionChecklist({ items }) {
  return (
    <div className="space-y-2">
      {items.map((item, i) => {
        const text = typeof item === "string" ? item : item.text;
        const essential = typeof item === "object" && item.essential;
        return (
          <div
            key={i}
            className="flex items-start gap-4 py-3.5 px-5"
            style={{
              background: essential ? `${C.red}08` : C.white,
              borderLeft: essential ? `3px solid ${C.red}` : `3px solid ${C.navy}10`,
              borderRadius: 2,
            }}
          >
            <CheckCircle2
              className="w-4.5 h-4.5 mt-0.5 flex-shrink-0"
              style={{ color: essential ? C.red : C.olive }}
              strokeWidth={2}
            />
            <div className="flex-1">
              <p className="text-sm font-medium" style={{ fontFamily: BODY, color: C.navy }}>{text}</p>
            </div>
            {essential && (
              <span className="text-[9px] font-bold tracking-wider uppercase flex-shrink-0 px-2 py-0.5"
                style={{ fontFamily: MONO, color: C.red, background: `${C.red}10`, borderRadius: 2 }}>
                CLAVE
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function PhaseCard({ children, color }) {
  const [h, setH] = useState(false);
  return (
    <div
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      className="p-5 md:p-6"
      style={{
        background: h ? `${color}12` : `${color}08`,
        border: `1px solid ${h ? color + "30" : color + "18"}`,
        borderRadius: 6,
        transform: h ? "translateY(-4px)" : "translateY(0)",
        transition: "transform 0.3s cubic-bezier(0.22,1,0.36,1), background 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease",
        boxShadow: h ? "0 8px 20px rgba(0,0,0,0.08)" : "none",
        cursor: "default",
      }}
    >
      {children}
    </div>
  );
}

function SectionPhases({ items, phase, color }) {
  const phaseColor = color || PHASE_COLORS[phase] || C.blue;
  const label = PHASE_LABELS[phase] || phase;
  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <span className="w-3 h-3" style={{ background: phaseColor, borderRadius: 2 }} />
        <span className="text-[10px] font-bold tracking-[0.2em]" style={{ fontFamily: MONO, color: phaseColor }}>{label}</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((item, i) => {
          const isObj = typeof item === "object";
          const itemTitle = isObj ? item.title : null;
          const desc = isObj ? item.desc : (typeof item === "string" ? item : item.text);
          return (
            <PhaseCard key={i} color={phaseColor}>
              <div className="flex items-start gap-3">
                <span className="text-xs font-bold flex-shrink-0 w-7 h-7 flex items-center justify-center" style={{ fontFamily: MONO, color: C.white, background: phaseColor, borderRadius: 4 }}>
                  {i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  {itemTitle && (
                    <p className="text-base font-bold mb-1" style={{ fontFamily: DISPLAY, color: C.navy }}>{itemTitle}</p>
                  )}
                  <p className="text-sm leading-[1.65]" style={{ fontFamily: BODY, color: itemTitle ? "#555" : C.navy }}>{desc}</p>
                </div>
              </div>
            </PhaseCard>
          );
        })}
      </div>
    </div>
  );
}

function WarningCard({ children }) {
  const [h, setH] = useState(false);
  return (
    <div
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      className="p-5 md:p-6"
      style={{
        background: h ? `${C.red}0A` : `${C.red}05`,
        border: `1px solid ${h ? C.red + "25" : C.red + "15"}`,
        borderRadius: 6,
        transform: h ? "translateY(-4px)" : "translateY(0)",
        transition: "transform 0.3s cubic-bezier(0.22,1,0.36,1), background 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease",
        boxShadow: h ? "0 8px 20px rgba(0,0,0,0.08)" : "none",
        cursor: "default",
      }}
    >
      {children}
    </div>
  );
}

function SectionWarnings({ items }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-6">
        <div className="w-8 h-8 flex items-center justify-center" style={{ background: `${C.red}12`, borderRadius: 6 }}>
          <AlertTriangle className="w-4 h-4" style={{ color: C.red }} />
        </div>
        <span className="text-[11px] font-bold tracking-[0.2em]" style={{ fontFamily: MONO, color: C.red }}>ATENCIÓN</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((item, i) => {
          const isObj = typeof item === "object" && item.title;
          const itemTitle = isObj ? item.title : null;
          const desc = isObj ? item.desc : (typeof item === "string" ? item : item.text || item);
          return (
            <WarningCard key={i}>
              <div className="flex items-start gap-3">
                <span className="w-7 h-7 flex-shrink-0 flex items-center justify-center mt-0.5" style={{ background: `${C.red}12`, borderRadius: 4 }}>
                  <AlertTriangle className="w-3.5 h-3.5" style={{ color: C.red }} />
                </span>
                <div className="flex-1 min-w-0">
                  {itemTitle && (
                    <p className="text-sm font-bold mb-1" style={{ fontFamily: DISPLAY, color: C.navy }}>{itemTitle}</p>
                  )}
                  <p className="text-sm leading-[1.65]" style={{ fontFamily: BODY, color: itemTitle ? "#555" : C.navy }}>{desc}</p>
                </div>
              </div>
            </WarningCard>
          );
        })}
      </div>
    </div>
  );
}

function SectionPhones({ phones }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {phones.map((p, i) => (
        <a key={i} href={`tel:${p.number}`} className="block p-8 md:p-10 text-center transition-all hover:-translate-y-1 hover:shadow-lg" style={{ background: C.navy, borderRadius: 6, textDecoration: "none" }}>
          <Phone className="w-6 h-6 mx-auto mb-3" style={{ color: C.orange }} />
          <p className="text-3xl md:text-4xl font-bold" style={{ fontFamily: DISPLAY, color: C.orange }}>{p.number}</p>
          <p className="text-[11px] mt-3 tracking-wider uppercase" style={{ fontFamily: MONO, color: "rgba(255,255,255,0.5)" }}>{p.label}</p>
        </a>
      ))}
    </div>
  );
}

function SectionCallout({ content, cta }) {
  const navigate = useNavigate();
  return (
    <div className="px-8 py-8" style={{ background: C.navy, borderRadius: 4 }}>
      <p className="text-base leading-relaxed mb-5" style={{ fontFamily: BODY, color: "rgba(255,255,255,0.85)" }}>{content}</p>
      {cta && (
        <button onClick={() => navigate(cta.href)}
          className="inline-flex items-center gap-2 px-6 py-3 font-semibold text-sm transition-all hover:-translate-y-0.5"
          style={{ fontFamily: BODY, background: C.orange, color: C.navy, border: "none", cursor: "pointer", borderRadius: 4 }}
          data-testid="article-cta-btn"
        >
          {cta.label} <ArrowUpRight className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

function SectionAnimals({ animals }) {
  const [active, setActive] = useState(0);
  return (
    <div>
      <div className="flex gap-2 mb-5 flex-wrap">
        {animals.map((a, i) => (
          <button key={i} onClick={() => setActive(i)}
            className="px-4 py-2 text-xs font-semibold tracking-wider transition-all"
            style={{
              fontFamily: BODY, borderRadius: 4, border: "none", cursor: "pointer",
              background: active === i ? a.color : `${C.navy}06`,
              color: active === i ? C.white : C.navy,
            }}
          >{a.name}</button>
        ))}
      </div>
      <div className="p-6" style={{ background: `${animals[active].color}08`, borderLeft: `3px solid ${animals[active].color}`, borderRadius: 4 }}>
        <ul className="space-y-3">
          {animals[active].tips.map((tip, i) => (
            <li key={i} className="flex items-start gap-3 text-sm" style={{ fontFamily: BODY, color: C.navy }}>
              <PawPrint className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: animals[active].color }} />
              <span style={{ lineHeight: 1.6 }}>{tip}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function SectionDownloadables({ downloadables }) {
  return (
    <div className="space-y-4">
      {downloadables.map((dl, i) => (
        <a key={i} href={dl.url} target="_blank" rel="noopener noreferrer"
          className="block transition-all hover:-translate-y-1" style={{ textDecoration: "none" }}
          data-testid={`download-${i}`}
        >
          <div className="grid md:grid-cols-12 overflow-hidden" style={{ border: `1px solid ${C.navy}15`, borderRadius: 4 }}>
            <div className="md:col-span-3 p-6 flex flex-col items-center justify-center gap-2" style={{ background: C.navy }}>
              <FileText className="w-8 h-8" style={{ color: C.orange }} />
              <span className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ fontFamily: MONO, color: C.orange }}>{dl.type}</span>
            </div>
            <div className="md:col-span-9 p-6 flex items-center gap-4" style={{ background: C.white }}>
              <div className="flex-1">
                <h4 className="text-base font-bold mb-1" style={{ fontFamily: DISPLAY, color: C.navy }}>{dl.title}</h4>
                <p className="text-sm" style={{ color: "#666", lineHeight: 1.6 }}>{dl.desc}</p>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 flex-shrink-0" style={{ background: `${C.orange}15`, borderRadius: 4 }}>
                <Download className="w-4 h-4" style={{ color: C.orange }} />
                <span className="text-xs font-bold" style={{ fontFamily: MONO, color: C.orange }}>DESCARGAR</span>
              </div>
            </div>
          </div>
        </a>
      ))}
    </div>
  );
}

/* ═══════════════════ RENDER DISPATCHER ═══════════════════ */

function RenderSection({ section }) {
  switch (section.type) {
    case "narrative": return <SectionNarrative content={section.content} lead={section.lead} />;
    case "image": return <SectionImage src={section.src} alt={section.alt} caption={section.caption} wide={section.wide} />;
    case "pullquote": return <SectionPullQuote text={section.text} attribution={section.attribution} color={section.color} />;
    case "stat": return <SectionStat items={section.items} bg={section.bg} />;
    case "colorblock": return <SectionColorBlock title={section.title} content={section.content} items={section.items} color={section.color} icon={section.icon ? AlertTriangle : null} />;
    case "split": return <SectionSplit left={section.left} right={section.right} reverse={section.reverse} />;
    case "text": return <SectionNarrative content={section.content} />;
    case "checklist": return <SectionChecklist items={section.items} />;
    case "phases": return <SectionPhases items={section.items} phase={section.phase} color={section.color} />;
    case "warnings": return <SectionWarnings items={section.items} />;
    case "phones": return <SectionPhones phones={section.phones} />;
    case "callout": return <SectionCallout content={section.content} cta={section.cta} />;
    case "animals": return <SectionAnimals animals={section.animals} />;
    default: return null;
  }
}

/* ═══════════════════ MAIN PAGE ═══════════════════ */

export default function BlogArticlePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const article = BLOG_ARTICLES.find(a => a.slug === slug);

  if (!article) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: C.white }}>
      <div className="text-center">
        <h1 className="text-3xl mb-4" style={{ fontFamily: DISPLAY, color: C.navy }}>Guía no encontrada</h1>
        <button onClick={() => navigate("/recursos/ciudadania/guias")} className="text-sm font-bold"
          style={{ fontFamily: BODY, color: C.blue, background: "none", border: "none", cursor: "pointer" }}>
          Volver a Guías
        </button>
      </div>
    </div>
  );

  const shareUrl = `${window.location.origin}/recursos/ciudadania/guias/${article.slug}`;
  const shareText = `${article.title} — Emergencia Animal`;

  return (
    <div className="min-h-screen" style={{ fontFamily: BODY, background: C.white }}>
      <SEO
        title={article.seoTitle || article.title}
        description={article.metaDescription || article.intro?.slice(0, 155)}
        path={`/recursos/ciudadania/guias/${article.slug}`}
        type="article"
        article={{ title: article.title, date: article.date, keywords: article.keywords }}
        breadcrumbs={[
          { name: "Inicio", url: "/" },
          { name: "Recursos", url: null },
          { name: "Ciudadanía", url: null },
          { name: "Guías", url: "/recursos/ciudadania/guias" },
          { name: article.title, url: `/recursos/ciudadania/guias/${article.slug}` },
        ]}
      />
      {/* NAV */}
      <header className="px-6 md:px-12 py-4" style={{ background: C.navy }}>
        <div className="max-w-[900px] mx-auto flex items-center justify-between">
          <button onClick={() => navigate("/recursos/ciudadania/guias")} className="flex items-center gap-2 text-sm font-semibold"
            style={{ fontFamily: BODY, color: C.blue, background: "none", border: "none", cursor: "pointer" }}
            data-testid="article-back-guias-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Guías
          </button>
          <button onClick={() => navigate("/")} className="flex items-center gap-2"
            style={{ background: "none", border: "none", cursor: "pointer" }} data-testid="article-home-link">
            <img src="/logos/logo-emergencia-animal.png" alt="EA" className="w-8 h-8 object-contain" style={{ borderRadius: 4 }} />
            <span className="text-sm font-bold hidden sm:block" style={{ color: C.white }}>Emergencia Animal</span>
          </button>
        </div>
      </header>

      {/* HERO */}
      <div className="px-6 md:px-12 pt-4 pb-12 md:pt-4 md:pb-20" style={{ background: article.categoryColor }}>
        <div className="max-w-[900px] mx-auto">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 mb-6" aria-label="Breadcrumb" data-testid="article-breadcrumb">
            {[
              { label: "Recursos", to: null },
              { label: "Ciudadanía", to: null },
              { label: "Guías", to: "/recursos/ciudadania/guias" },
            ].map((crumb, i) => (
              <span key={i} className="flex items-center gap-2">
                {i > 0 && <ChevronRight className="w-3 h-3" style={{ color: "rgba(255,255,255,0.3)" }} />}
                {crumb.to ? (
                  <button onClick={() => navigate(crumb.to)}
                    className="text-[10px] font-semibold tracking-[0.15em] uppercase"
                    style={{ fontFamily: MONO, color: "rgba(255,255,255,0.7)", background: "none", border: "none", cursor: "pointer" }}
                  >{crumb.label}</button>
                ) : (
                  <span className="text-[10px] font-semibold tracking-[0.15em] uppercase"
                    style={{ fontFamily: MONO, color: "rgba(255,255,255,0.4)" }}>{crumb.label}</span>
                )}
              </span>
            ))}
          </nav>
          <div className="flex items-center gap-3 mb-6">
            <span className="text-[10px] font-bold tracking-[0.2em] uppercase px-3 py-1.5"
              style={{ fontFamily: MONO, background: "rgba(255,255,255,0.2)", color: C.white, borderRadius: 2 }}>
              {article.category}
            </span>
            <span className="text-[10px] flex items-center gap-1" style={{ fontFamily: MONO, color: "rgba(255,255,255,0.6)" }}>
              <Clock className="w-3 h-3" /> {article.readTime}
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight mb-4" style={{ fontFamily: DISPLAY, color: C.white }} data-testid="article-title">
            {article.title}
          </h1>
          <p className="text-lg max-w-xl" style={{ color: "rgba(255,255,255,0.8)" }}>{article.subtitle}</p>
          <div className="flex items-center gap-4 mt-8">
            <span className="text-[10px] flex items-center gap-1" style={{ fontFamily: MONO, color: "rgba(255,255,255,0.5)" }}>
              <Calendar className="w-3 h-3" /> {formatDate(article.date)}
            </span>
            <span className="text-[10px]" style={{ fontFamily: MONO, color: "rgba(255,255,255,0.5)" }}>·</span>
            <span className="text-[10px]" style={{ fontFamily: MONO, color: "rgba(255,255,255,0.5)" }}>{article.author}</span>
          </div>
        </div>
      </div>

      {/* ARTICLE BODY */}
      <article className="max-w-[900px] mx-auto px-6 md:px-12 py-12 md:py-16" data-testid="article-body">
        {/* Intro */}
        <div className="mb-12 pb-8" style={{ borderBottom: `1px solid ${C.navy}10` }}>
          <p className="text-lg leading-[1.8]" style={{ fontFamily: BODY, color: C.navy }}>
            {article.intro}
          </p>
        </div>

        {/* Share */}
        <div className="flex items-center gap-3 mb-12">
          <Share2 className="w-4 h-4" style={{ color: "#999" }} />
          <span className="text-[10px] font-bold tracking-[0.15em] uppercase" style={{ fontFamily: MONO, color: "#999" }}>Compartir:</span>
          <ShareButtonsRow text={shareText} url={shareUrl} shareType="blog" resourceId={article.slug} size="sm" />
        </div>

        {/* Sections */}
        <div className="space-y-14">
          {article.sections.map((section, i) => (
            <RevealOnScroll key={i} delay={0.04}>
              <section data-testid={`article-section-${i}`}>
                {section.type !== "pullquote" && section.type !== "image" && section.type !== "stat" && section.type !== "colorblock" && section.title && (
                  <div className="flex items-center gap-3 mb-6">
                    <span className="text-xs font-semibold tracking-widest" style={{ fontFamily: MONO, color: article.categoryColor }}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="w-8 h-px" style={{ background: article.categoryColor }} />
                    <h2 className="text-xl md:text-2xl font-bold tracking-tight" style={{ fontFamily: DISPLAY, color: C.navy }}>
                      {section.title}
                    </h2>
                  </div>
                )}
                <RenderSection section={section} />
              </section>
            </RevealOnScroll>
          ))}
        </div>

        {/* Downloadables */}
        {article.downloadables && article.downloadables.length > 0 && (
          <div className="mt-16 pt-10" style={{ borderTop: `1px solid ${C.navy}15` }}>
            <div className="flex items-center gap-3 mb-6">
              <Download className="w-5 h-5" style={{ color: C.orange }} />
              <h2 className="text-xl font-bold" style={{ fontFamily: DISPLAY, color: C.navy }}>Material descargable</h2>
            </div>
            <SectionDownloadables downloadables={article.downloadables} />
          </div>
        )}
      </article>

      {/* RELATED / BACK */}
      {article.related && article.related.length > 0 && (
        <section className="px-6 md:px-12 py-12" style={{ background: "#f8f7f4" }}>
          <div className="max-w-[900px] mx-auto">
            <p className="text-[10px] font-bold tracking-[0.2em] uppercase mb-6" style={{ fontFamily: MONO, color: C.navy }}>Artículos relacionados</p>
            <div className="space-y-3">
              {article.related.map((r, i) => {
                const related = BLOG_ARTICLES.find(a => a.slug === r);
                if (!related) return null;
                return (
                  <div key={i} className="flex items-center gap-4 py-3 cursor-pointer transition-all hover:-translate-y-0.5"
                    style={{ borderBottom: `1px solid ${C.navy}10` }}
                    onClick={() => navigate(`/recursos/ciudadania/guias/${r}`)} data-testid={`related-article-${i}`}>
                    <span className="w-2 h-2" style={{ background: related.categoryColor, borderRadius: 2 }} />
                    <h3 className="text-sm font-bold flex-1" style={{ fontFamily: DISPLAY, color: C.navy }}>{related.title}</h3>
                    <ArrowRight className="w-4 h-4" style={{ color: C.navy }} />
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Teaming CTA */}
      <section className="px-6 md:px-12 py-12" style={{ background: "#f8f7f4" }}>
        <div className="max-w-[900px] mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-6 p-6 md:p-8"
            style={{ background: C.white, border: `1px solid ${C.navy}08`, borderRadius: 4 }}>
            <div className="flex-1">
              <p className="text-[10px] font-bold tracking-[0.2em] uppercase mb-2" style={{ fontFamily: MONO, color: C.orange }}>
                PROYECTO ALTRUISTA
              </p>
              <p className="text-sm leading-relaxed" style={{ color: "#64748b" }}>
                Emergencia Animal es gratuito y sin publicidad.
                <strong style={{ color: C.navy }}> Con 1€/mes en Teaming</strong> ayudas a mantener las guías actualizadas y la plataforma activa.
              </p>
            </div>
            <a href="https://www.teaming.net/emergenciaanimal" target="_blank" rel="noopener noreferrer"
              data-testid="article-teaming-btn"
              className="shrink-0 inline-flex items-center gap-2 px-6 py-3 text-xs font-bold uppercase tracking-wider transition-all hover:-translate-y-0.5"
              style={{
                fontFamily: MONO,
                background: C.orange,
                color: C.navy,
                border: "none",
                borderRadius: 4,
                textDecoration: "none",
              }}>
              <Heart className="w-3.5 h-3.5 fill-current" /> Colaborar
            </a>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="px-6 md:px-12 py-16" style={{ background: C.navy }}>
        <div className="max-w-[900px] mx-auto text-center">
          <p className="text-2xl md:text-3xl leading-tight mb-6" style={{ fontFamily: DISPLAY, color: C.white }}>
            Prepararse no es tener miedo.<br />
            <span style={{ color: C.orange }}>Es tener un plan.</span>
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button onClick={() => navigate("/prepara")}
              className="px-8 py-3 text-sm font-bold flex items-center gap-2"
              style={{ fontFamily: BODY, background: C.orange, color: C.navy, border: "none", cursor: "pointer", borderRadius: 4 }}>
              Crear mi plan <ArrowRight className="w-4 h-4" />
            </button>
            <button onClick={() => navigate("/recursos/ciudadania/guias")}
              className="px-8 py-3 text-sm font-bold flex items-center gap-2"
              style={{ fontFamily: BODY, background: "transparent", color: C.white, border: `1px solid rgba(255,255,255,0.3)`, cursor: "pointer", borderRadius: 4 }}>
              Más guías
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
